import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    TextField,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Tooltip,
    LinearProgress,
    Alert,
    AlertTitle,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    Stack,
    InputAdornment,
} from '@mui/material';
import {
    Refresh,
    CheckCircle,
    Error as ErrorIcon,
    Warning,
    CloudDownload,
    Search,
    Add,
    PlayArrow,
    BarChart,
    Storage,
    DateRange,
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import { authHeaders } from '../auth/apiClient';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:7071';

const authGet = async (url) => axios.get(url, { headers: await authHeaders() });
const authPost = async (url, body) => axios.post(url, body, {
    headers: await authHeaders({ 'Content-Type': 'application/json' }),
    timeout: 180000,
});

// ── helpers ───────────────────────────────────────────────────────────────────

// India FY ends March 31 — month > 3 means April onwards, so that FY is complete
const _now = new Date();
const CURRENT_FY = _now.getMonth() >= 3 ? _now.getFullYear() : _now.getFullYear() - 1;

function StatusChip({ inDB, yearCount, latestYear, missingYears }) {
    if (!inDB) {
        return <Chip label="Not in DB" color="error" size="small" icon={<ErrorIcon />} />;
    }
    const hasMissing = missingYears && missingYears.length > 0;
    if (!latestYear || latestYear < CURRENT_FY) {
        return <Chip label="Outdated" color="warning" size="small" icon={<Warning />} />;
    }
    if (hasMissing) {
        return <Chip label={`${missingYears.length} gaps`} color="warning" size="small" icon={<Warning />} />;
    }
    return <Chip label={`${yearCount} years`} color="success" size="small" icon={<CheckCircle />} />;
}

function YearBadges({ years }) {
    if (!years || years.length === 0) return <Typography variant="caption" color="text.secondary">—</Typography>;
    const sorted = [...years].sort();
    const latest5 = sorted.slice(-5);
    const extra = sorted.length - latest5.length;
    return (
        <Stack direction="row" spacing={0.5} flexWrap="wrap">
            {extra > 0 && (
                <Chip label={`+${extra}`} size="small" sx={{ fontSize: '0.65rem', height: 18 }} />
            )}
            {latest5.map(y => (
                <Chip
                    key={y}
                    label={y}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.65rem', height: 18 }}
                />
            ))}
        </Stack>
    );
}

// ── main component ────────────────────────────────────────────────────────────

const DataScrapeAdmin = () => {
    const navigate = useNavigate();
    const [companies, setCompanies] = useState([]);
    const [summary, setSummary] = useState({ total: 0, inDB: 0, notInDB: 0 });
    const [loading, setLoading] = useState(false);
    const [scrapingTicker, setScrapingTicker] = useState(null);   // ticker currently being scraped
    const [scrapingQuarterlyTicker, setScrapingQuarterlyTicker] = useState(null); // quarterly scrape in progress
    const [batchRunning, setBatchRunning] = useState(false);
    const [batchProgress, setBatchProgress] = useState({ done: 0, total: 0, current: '' });
    const [batchQuarterlyRunning, setBatchQuarterlyRunning] = useState(false);
    const [batchQuarterlyProgress, setBatchQuarterlyProgress] = useState({ done: 0, total: 0, current: '' });
    const [search, setSearch] = useState('');
    const [addTickerInput, setAddTickerInput] = useState('');
    const [addingTicker, setAddingTicker] = useState(false);

    // Detail dialog
    const [detailOpen, setDetailOpen] = useState(false);
    const [detailCompany, setDetailCompany] = useState(null);

    // ── fetch companies list ─────────────────────────────────────────────────
    const fetchCompanies = useCallback(async () => {
        setLoading(true);
        try {
            const res = await authGet(`${API_BASE}/api/scrape/admin/companies`);
            const data = res.data?.data || [];
            setCompanies(data);
            setSummary({
                total: res.data?.total || data.length,
                inDB: res.data?.inDB || 0,
                notInDB: res.data?.notInDB || 0,
            });
        } catch (err) {
            toast.error('Failed to load company list');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCompanies();
    }, [fetchCompanies]);

    // ── scrape single company ────────────────────────────────────────────────
    const scrapeCompany = async (ticker, force = false) => {
        setScrapingTicker(ticker);
        try {
            const res = await authPost(`${API_BASE}/api/scrape/admin/scrape`, { ticker, force });
            const result = res.data?.data;
            if (result?.status === 'cached') {
                toast.info(`${ticker}: Already up to date (${result.companyStatus?.latestYear}). Use Force Re-scrape to refresh.`);
            } else {
                toast.success(`${ticker}: Scraped successfully — ${result?.companyStatus?.yearCount} years`);
            }
            // Refresh just this row
            await fetchCompanies();
        } catch (err) {
            const msg = err.response?.data?.details || err.response?.data?.message || err.message;
            toast.error(`${ticker}: Scrape failed — ${msg}`);
        } finally {
            setScrapingTicker(null);
        }
    };

    // ── batch scrape ─────────────────────────────────────────────────────────
    const runBatchScrape = async () => {
        const pending = companies.filter(c => !c.inDB || !c.latestYear || c.latestYear < CURRENT_FY || c.missingYears?.length > 0);
        if (pending.length === 0) {
            toast.info('All companies are up to date!');
            return;
        }
        setBatchRunning(true);
        setBatchProgress({ done: 0, total: pending.length, current: '' });

        for (let i = 0; i < pending.length; i++) {
            const ticker = pending[i].ticker;
            setBatchProgress({ done: i, total: pending.length, current: ticker });
            try {
                await authPost(`${API_BASE}/api/scrape/admin/scrape`, { ticker, force: false });
                // 5-second polite gap between companies
                await new Promise(r => setTimeout(r, 5000));
            } catch (err) {
                // 503 means Screener.in is blocking this IP — stop immediately
                if (err.response?.status === 503 && err.response?.data?.status === 'blocked') {
                    setBatchRunning(false);
                    setBatchProgress(prev => ({ ...prev, current: '' }));
                    toast.error(
                        'Screener.in is blocking this IP (anti-bot). ' +
                        'Please wait 15–30 minutes, then restart the batch.',
                        { autoClose: false }
                    );
                    await fetchCompanies();
                    return;
                }
                console.error(`Batch: failed ${ticker}`, err);
            }
        }

        setBatchProgress(prev => ({ ...prev, done: pending.length, current: '' }));
        setBatchRunning(false);
        toast.success(`Batch complete — processed ${pending.length} companies`);
        await fetchCompanies();
    };

    // ── scrape quarterly for single company ──────────────────────────────────
    const scrapeQuarterly = async (ticker) => {
        setScrapingQuarterlyTicker(ticker);
        try {
            const res = await authPost(`${API_BASE}/api/scrape/admin/scrape-quarterly`, { ticker });
            const result = res.data?.data;
            if (result?.periodsScraped === 0) {
                toast.warning(`${ticker}: No quarterly columns found on Screener.in (may not be available).`);
            } else {
                toast.success(`${ticker}: ${result?.periodsScraped} quarterly periods scraped — latest: ${result?.quarterly?.latestQuarter}`);
            }
            await fetchCompanies();
        } catch (err) {
            if (err.response?.status === 503 && err.response?.data?.status === 'blocked') {
                toast.error('Screener.in is blocking this IP. Wait 15–30 minutes, then retry.', { autoClose: false });
            } else {
                const msg = err.response?.data?.details || err.response?.data?.message || err.message;
                toast.error(`${ticker}: Quarterly scrape failed — ${msg}`);
            }
        } finally {
            setScrapingQuarterlyTicker(null);
        }
    };

    // ── batch quarterly scrape (all companies) ───────────────────────────────
    const runBatchQuarterlyScrape = async () => {
        const targets = companies.filter(c => c.inDB);
        if (targets.length === 0) {
            toast.info('No companies in DB to scrape quarterly data for.');
            return;
        }
        setBatchQuarterlyRunning(true);
        setBatchQuarterlyProgress({ done: 0, total: targets.length, current: '' });

        for (let i = 0; i < targets.length; i++) {
            const ticker = targets[i].ticker;
            setBatchQuarterlyProgress({ done: i, total: targets.length, current: ticker });
            try {
                await authPost(`${API_BASE}/api/scrape/admin/scrape-quarterly`, { ticker });
                await new Promise(r => setTimeout(r, 5000));
            } catch (err) {
                if (err.response?.status === 503 && err.response?.data?.status === 'blocked') {
                    setBatchQuarterlyRunning(false);
                    setBatchQuarterlyProgress(prev => ({ ...prev, current: '' }));
                    toast.error(
                        'Screener.in is blocking this IP (anti-bot). ' +
                        'Please wait 15–30 minutes, then restart the batch.',
                        { autoClose: false }
                    );
                    await fetchCompanies();
                    return;
                }
                console.error(`Quarterly batch: failed ${ticker}`, err);
            }
        }

        setBatchQuarterlyProgress(prev => ({ ...prev, done: targets.length, current: '' }));
        setBatchQuarterlyRunning(false);
        toast.success(`Quarterly batch complete — processed ${targets.length} companies`);
        await fetchCompanies();
    };

    // ── add ticker ───────────────────────────────────────────────────────────
    const handleAddTicker = async () => {
        const tickers = addTickerInput
            .toUpperCase()
            .split(/[\s,;]+/)
            .map(t => t.trim())
            .filter(Boolean);
        if (!tickers.length) return;

        setAddingTicker(true);
        try {
            await authPost(`${API_BASE}/api/scrape/admin/tickers`, { tickers });
            toast.success(`Added: ${tickers.join(', ')} — starting annual + quarterly scrape…`);
            setAddTickerInput('');
            await fetchCompanies();

            // Auto-scrape annual and quarterly for each new ticker
            for (const ticker of tickers) {
                try {
                    await authPost(`${API_BASE}/api/scrape/admin/scrape`, { ticker, force: false });
                    toast.success(`${ticker}: Annual data scraped`);
                } catch (err) {
                    toast.error(`${ticker}: Annual scrape failed — ${err.response?.data?.details || err.message}`);
                }
                try {
                    const qRes = await authPost(`${API_BASE}/api/scrape/admin/scrape-quarterly`, { ticker });
                    const periods = qRes.data?.data?.periodsScraped || 0;
                    if (periods > 0) {
                        toast.success(`${ticker}: ${periods} quarterly periods scraped`);
                    }
                } catch (err) {
                    toast.warning(`${ticker}: Quarterly scrape failed — ${err.response?.data?.details || err.message}`);
                }
                await new Promise(r => setTimeout(r, 3000));
            }
            await fetchCompanies();
        } catch (err) {
            toast.error('Failed to add tickers');
        } finally {
            setAddingTicker(false);
        }
    };

    // ── open detail dialog ───────────────────────────────────────────────────
    const openDetail = (company) => {
        setDetailCompany(company);
        setDetailOpen(true);
    };

    // ── filtered list ────────────────────────────────────────────────────────
    const filtered = companies.filter(c => {
        if (!search) return true;
        const q = search.toUpperCase();
        return (
            c.ticker.includes(q) ||
            (c.companyName || '').toUpperCase().includes(q) ||
            (c.sector || '').toUpperCase().includes(q)
        );
    });

    // ── render ────────────────────────────────────────────────────────────────
    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
            <Button
                variant="text"
                onClick={() => navigate('/home')}
                sx={{ mb: 1, textTransform: 'none', fontWeight: 600 }}
            >
                ← Home
            </Button>

            {/* ── Header ── */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography variant="h5" fontWeight="bold">
                        Data Scrape Manager
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Scrape financial data from Screener.in and store in MongoDB
                    </Typography>
                </Box>
                <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={fetchCompanies}
                    disabled={loading}
                >
                    Refresh
                </Button>
            </Box>

            {/* ── Summary Cards ── */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
                <Card sx={{ flex: 1 }}>
                    <CardContent sx={{ py: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Storage color="primary" />
                            <Box>
                                <Typography variant="h4" fontWeight="bold">{summary.total}</Typography>
                                <Typography variant="caption" color="text.secondary">Tracked Companies</Typography>
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>
                <Card sx={{ flex: 1 }}>
                    <CardContent sx={{ py: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <CheckCircle color="success" />
                            <Box>
                                <Typography variant="h4" fontWeight="bold" color="success.main">{summary.inDB}</Typography>
                                <Typography variant="caption" color="text.secondary">In MongoDB</Typography>
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>
                <Card sx={{ flex: 1 }}>
                    <CardContent sx={{ py: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <ErrorIcon color="error" />
                            <Box>
                                <Typography variant="h4" fontWeight="bold" color="error.main">{summary.notInDB}</Typography>
                                <Typography variant="caption" color="text.secondary">Not Scraped Yet</Typography>
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>
                <Card sx={{ flex: 1 }}>
                    <CardContent sx={{ py: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Warning color="warning" />
                            <Box>
                                <Typography variant="h4" fontWeight="bold" color="warning.main">
                                    {companies.filter(c => c.inDB && c.missingYears?.length > 0).length}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">Have Year Gaps</Typography>
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>
                <Card sx={{ flex: 1 }}>
                    <CardContent sx={{ py: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <DateRange color="info" />
                            <Box>
                                <Typography variant="h4" fontWeight="bold" color="info.main">
                                    {companies.filter(c => c.quarterly?.hasQuarterly).length}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">Have Quarterly Data</Typography>
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>
            </Stack>

            {/* ── Batch Progress ── */}
            {batchRunning && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    <AlertTitle>Batch Scrape Running</AlertTitle>
                    <Box sx={{ mt: 1 }}>
                        <Typography variant="caption">
                            {batchProgress.done} / {batchProgress.total} companies
                            {batchProgress.current && ` — currently: ${batchProgress.current}`}
                        </Typography>
                        <LinearProgress
                            variant="determinate"
                            value={batchProgress.total > 0 ? (batchProgress.done / batchProgress.total) * 100 : 0}
                            sx={{ mt: 1, borderRadius: 1 }}
                        />
                    </Box>
                </Alert>
            )}

            {batchQuarterlyRunning && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    <AlertTitle>Quarterly Batch Scrape Running</AlertTitle>
                    <Box sx={{ mt: 1 }}>
                        <Typography variant="caption">
                            {batchQuarterlyProgress.done} / {batchQuarterlyProgress.total} companies
                            {batchQuarterlyProgress.current && ` — currently: ${batchQuarterlyProgress.current}`}
                        </Typography>
                        <LinearProgress
                            variant="determinate"
                            value={batchQuarterlyProgress.total > 0 ? (batchQuarterlyProgress.done / batchQuarterlyProgress.total) * 100 : 0}
                            sx={{ mt: 1, borderRadius: 1 }}
                            color="secondary"
                        />
                    </Box>
                </Alert>
            )}

            {/* ── Add Ticker + Batch Actions ── */}
            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
                        {/* Add Ticker */}
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                                Add Ticker(s) to Watch List
                            </Typography>
                            <Stack direction="row" spacing={1}>
                                <TextField
                                    size="small"
                                    placeholder="e.g. ZOMATO, NYKAA, PAYTM"
                                    value={addTickerInput}
                                    onChange={e => setAddTickerInput(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && handleAddTicker()}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Add fontSize="small" />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{ flex: 1 }}
                                    disabled={addingTicker}
                                />
                                <Button
                                    variant="contained"
                                    onClick={handleAddTicker}
                                    disabled={!addTickerInput.trim() || addingTicker}
                                    startIcon={addingTicker ? <CircularProgress size={14} /> : <Add />}
                                >
                                    Add
                                </Button>
                            </Stack>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                Comma or space separated. Uses Screener.in NSE ticker slugs.
                            </Typography>
                        </Box>

                        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />

                        {/* Batch Actions */}
                        <Box>
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                                Batch Actions
                            </Typography>
                            <Stack direction="row" spacing={1}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={batchRunning ? <CircularProgress size={14} color="inherit" /> : <CloudDownload />}
                                    onClick={runBatchScrape}
                                    disabled={batchRunning || batchQuarterlyRunning || loading}
                                >
                                    Scrape Missing / Outdated
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    startIcon={batchQuarterlyRunning ? <CircularProgress size={14} color="inherit" /> : <DateRange />}
                                    onClick={runBatchQuarterlyScrape}
                                    disabled={batchRunning || batchQuarterlyRunning || loading}
                                >
                                    Scrape All Quarterly
                                </Button>
                            </Stack>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                Annual: skips up-to-date companies. Quarterly: scrapes all companies in DB.
                            </Typography>
                        </Box>
                    </Stack>
                </CardContent>
            </Card>

            {/* ── Search ── */}
            <TextField
                size="small"
                fullWidth
                placeholder="Search by ticker, company name, or sector..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <Search fontSize="small" />
                        </InputAdornment>
                    ),
                }}
                sx={{ mb: 2 }}
            />

            {/* ── Company Table ── */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Ticker</TableCell>
                                <TableCell>Company</TableCell>
                                <TableCell>Sector</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Latest Year</TableCell>
                                <TableCell>Recent Years</TableCell>
                                <TableCell>Missing Years</TableCell>
                                <TableCell>Quarterly</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filtered.map(company => {
                                const isScraping = scrapingTicker === company.ticker;
                                return (
                                    <TableRow
                                        key={company.ticker}
                                        hover
                                        sx={{ opacity: isScraping ? 0.6 : 1 }}
                                    >
                                        {/* Ticker */}
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                fontWeight="bold"
                                                fontFamily="monospace"
                                                sx={{ cursor: 'pointer', color: 'primary.main' }}
                                                onClick={() => openDetail(company)}
                                            >
                                                {company.ticker}
                                            </Typography>
                                        </TableCell>

                                        {/* Company Name */}
                                        <TableCell>
                                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                                {company.companyName || '—'}
                                            </Typography>
                                        </TableCell>

                                        {/* Sector */}
                                        <TableCell>
                                            {company.sector ? (
                                                <Chip label={company.sector} size="small" variant="outlined" sx={{ fontSize: '0.68rem' }} />
                                            ) : <Typography variant="caption" color="text.secondary">—</Typography>}
                                        </TableCell>

                                        {/* Status */}
                                        <TableCell>
                                            <StatusChip
                                                inDB={company.inDB}
                                                yearCount={company.yearCount}
                                                latestYear={company.latestYear}
                                                missingYears={company.missingYears}
                                            />
                                        </TableCell>

                                        {/* Latest Year */}
                                        <TableCell>
                                            <Typography variant="body2">
                                                {company.latestYear || '—'}
                                            </Typography>
                                        </TableCell>

                                        {/* Recent Years */}
                                        <TableCell sx={{ minWidth: 180 }}>
                                            <YearBadges years={company.yearsAvailable} />
                                        </TableCell>

                                        {/* Missing Years */}
                                        <TableCell sx={{ minWidth: 120 }}>
                                            {company.missingYears?.length > 0 ? (
                                                <Typography variant="caption" color="warning.main">
                                                    {company.missingYears.join(', ')}
                                                </Typography>
                                            ) : (
                                                company.inDB
                                                    ? <Typography variant="caption" color="success.main">None</Typography>
                                                    : <Typography variant="caption" color="text.disabled">—</Typography>
                                            )}
                                        </TableCell>

                                        {/* Quarterly Status */}
                                        <TableCell sx={{ minWidth: 130 }}>
                                            {company.quarterly?.hasQuarterly ? (
                                                <Stack spacing={0.3}>
                                                    <Typography variant="caption" color="info.main" fontWeight="bold">
                                                        {company.quarterly.latestQuarter}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {company.quarterly.quarterCount} qtrs
                                                    </Typography>
                                                </Stack>
                                            ) : (
                                                <Typography variant="caption" color="text.disabled">No quarterly</Typography>
                                            )}
                                        </TableCell>

                                        {/* Actions */}
                                        <TableCell align="right">
                                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                {/* Validate */}
                                                <Tooltip title="View details">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => openDetail(company)}
                                                    >
                                                        <BarChart fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>

                                                {/* Scrape */}
                                                <Tooltip title={company.inDB ? "Re-scrape annual (smart)" : "Scrape annual now"}>
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={() => scrapeCompany(company.ticker, false)}
                                                            disabled={isScraping || batchRunning || batchQuarterlyRunning}
                                                        >
                                                            {isScraping
                                                                ? <CircularProgress size={14} />
                                                                : <PlayArrow fontSize="small" />
                                                            }
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>

                                                {/* Scrape Quarterly */}
                                                <Tooltip title="Scrape quarterly data (Jun/Sep/Dec)">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            color="secondary"
                                                            onClick={() => scrapeQuarterly(company.ticker)}
                                                            disabled={scrapingQuarterlyTicker === company.ticker || batchRunning || batchQuarterlyRunning}
                                                        >
                                                            {scrapingQuarterlyTicker === company.ticker
                                                                ? <CircularProgress size={14} />
                                                                : <DateRange fontSize="small" />
                                                            }
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>

                                                {/* Force re-scrape */}
                                                <Tooltip title="Force full re-scrape (overwrites existing)">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            color="warning"
                                                            onClick={() => scrapeCompany(company.ticker, true)}
                                                            disabled={isScraping || batchRunning || batchQuarterlyRunning}
                                                        >
                                                            {isScraping
                                                                ? <CircularProgress size={14} />
                                                                : <Refresh fontSize="small" />
                                                            }
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}

                            {filtered.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                                        <Typography color="text.secondary">
                                            {search ? `No companies matching "${search}"` : 'No companies in watch list yet.'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* ── Detail Dialog ── */}
            <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="sm" fullWidth>
                {detailCompany && (
                    <>
                        <DialogTitle>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography variant="h6" fontWeight="bold" fontFamily="monospace">
                                    {detailCompany.ticker}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {detailCompany.companyName}
                                </Typography>
                            </Stack>
                        </DialogTitle>
                        <DialogContent dividers>
                            <Stack spacing={2}>
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">Sector</Typography>
                                    <Typography>{detailCompany.sector || '—'}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                                    <StatusChip
                                        inDB={detailCompany.inDB}
                                        yearCount={detailCompany.yearCount}
                                        latestYear={detailCompany.latestYear}
                                        missingYears={detailCompany.missingYears}
                                    />
                                </Box>
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                        Available Years ({detailCompany.yearsAvailable?.length || 0})
                                    </Typography>
                                    <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                                        {(detailCompany.yearsAvailable || []).map(y => (
                                            <Chip key={y} label={y} size="small" color="success" variant="outlined" />
                                        ))}
                                        {(!detailCompany.yearsAvailable?.length) && (
                                            <Typography variant="caption" color="text.secondary">None</Typography>
                                        )}
                                    </Stack>
                                </Box>
                                {detailCompany.missingYears?.length > 0 && (
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                            Missing Years ({detailCompany.missingYears.length})
                                        </Typography>
                                        <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                                            {detailCompany.missingYears.map(y => (
                                                <Chip key={y} label={y} size="small" color="warning" />
                                            ))}
                                        </Stack>
                                    </Box>
                                )}
                                {detailCompany.marketCap && (
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary">Market Cap</Typography>
                                        <Typography>
                                            ₹{(detailCompany.marketCap / 1e7).toFixed(0)} Cr
                                        </Typography>
                                    </Box>
                                )}
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                        Quarterly Data
                                    </Typography>
                                    {detailCompany.quarterly?.hasQuarterly ? (
                                        <Stack spacing={0.5}>
                                            <Typography variant="body2">
                                                Latest: <strong>{detailCompany.quarterly.latestQuarter}</strong>
                                                {' '}({detailCompany.quarterly.quarterCount} periods)
                                            </Typography>
                                            <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                                                {(detailCompany.quarterly.recentQuarters || []).map(q => (
                                                    <Chip key={q} label={q} size="small" color="info" variant="outlined" />
                                                ))}
                                            </Stack>
                                        </Stack>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            No quarterly data scraped yet.
                                        </Typography>
                                    )}
                                </Box>
                            </Stack>
                        </DialogContent>
                        <DialogActions sx={{ px: 3, py: 2 }}>
                            <Button onClick={() => setDetailOpen(false)}>Close</Button>
                            <Button
                                variant="outlined"
                                color="secondary"
                                startIcon={<DateRange />}
                                onClick={() => { setDetailOpen(false); scrapeQuarterly(detailCompany.ticker); }}
                                disabled={scrapingQuarterlyTicker === detailCompany.ticker}
                            >
                                Scrape Quarterly
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<PlayArrow />}
                                onClick={() => { setDetailOpen(false); scrapeCompany(detailCompany.ticker, false); }}
                                disabled={scrapingTicker === detailCompany.ticker}
                            >
                                Smart Scrape
                            </Button>
                            <Button
                                variant="contained"
                                color="warning"
                                startIcon={<Refresh />}
                                onClick={() => { setDetailOpen(false); scrapeCompany(detailCompany.ticker, true); }}
                                disabled={scrapingTicker === detailCompany.ticker}
                            >
                                Force Re-Scrape
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </Box>
    );
};

export default DataScrapeAdmin;
